// src/components/Center Admin/forms/OralImagingForm.tsx

import React, { useEffect, useState } from "react";
import api from "../../../api/axiosConfig";
import "../../../center admin css/OralImagingForm.css";
import "../../../center admin css/centerAdmin.css";

import S3ImageUploadBox from "../Reuse/S-3ImageUploadBox";
import { UploadedImageInfo } from "../../../hooks/useS3Uploader";

import anteriorImg from "../../../assets/images/Anterior.jpg";
import rightBuccalImg from "../../../assets/images/Right-buccal.jpg";
import leftBuccalImg from "../../../assets/images/Left Buccal.jpg";
import maxillaryImg from "../../../assets/images/Maxillary Occlusal.jpg";
import mandibularImg from "../../../assets/images/Mandibular Occlusal.jpg";
import mallampatiImg from "../../../assets/images/Mallampati Score.png";

interface FormComponentProps {
  emrId: string;
  onComplete?: () => void;
}

type OralKey =
  | "anterior"
  | "rightBuccal"
  | "leftBuccal"
  | "maxOcclusal"
  | "mandOcclusal"
  | "mallampati";

const ORAL_LABELS: Record<OralKey, string> = {
  anterior: "Anterior",
  rightBuccal: "Right Buccal",
  leftBuccal: "Left Buccal",
  maxOcclusal: "Maxillary Occlusal",
  mandOcclusal: "Mandibular Occlusal",
  mallampati: "Mallampati Score",
};

const ORAL_DEFAULT_IMAGES: Record<OralKey, string> = {
  anterior: anteriorImg,
  rightBuccal: rightBuccalImg,
  leftBuccal: leftBuccalImg,
  maxOcclusal: maxillaryImg,
  mandOcclusal: mandibularImg,
  mallampati: mallampatiImg,
};

const ORAL_S3_MODULE_MAP: Record<OralKey, string> = {
  anterior: "anterior",
  rightBuccal: "right-buccal",
  leftBuccal: "left-buccal",
  maxOcclusal: "maxillary",
  mandOcclusal: "mandibular",
  mallampati: "mallampati",
};

export const OralImagingForm: React.FC<FormComponentProps> = ({
  emrId,
  onComplete,
}) => {
  console.log("The EMR ID for this form is:", emrId);

  // STORE MULTIPLE URLs PER FIELD
  // const [uploadedUrls, setUploadedUrls] = useState<Record<OralKey, string[]>>({
  const [uploadedUrls, setUploadedUrls] = useState<
    Record<OralKey, UploadedImageInfo[]>
  >({
    anterior: [],
    rightBuccal: [],
    leftBuccal: [],
    maxOcclusal: [],
    mandOcclusal: [],
    mallampati: [],
  });

  const [loading, setLoading] = useState(false);
  const [resetTrigger, setResetTrigger] = useState(0);

  const removeUploadedImage = (key: OralKey, index: number) => {
    setUploadedUrls((prev) => ({
      ...prev,
      [key]: prev[key].filter((_, i) => i !== index),
    }));
  };

  /* ---------------------------------------------------------
      LOAD EXISTING ORAL IMAGING
  ---------------------------------------------------------- */

  const loadExistingOralImaging = async () => {
    if (!emrId) return;

    // Clear previous patient's images immediately
    setUploadedUrls({
      anterior: [],
      rightBuccal: [],
      leftBuccal: [],
      maxOcclusal: [],
      mandOcclusal: [],
      mallampati: [],
    });

    try {
      const res = await api.dataCollectionApi.get(
        `/api/forms/oral-imaging/${encodeURIComponent(emrId)}`,
      );

      const saved = res.data?.data;

      if (!saved) {
        return;
      }

      const savedOralImaging = saved["Oral Imaging"] ?? {};

      const restored: Record<OralKey, UploadedImageInfo[]> = {
        anterior: [],
        rightBuccal: [],
        leftBuccal: [],
        maxOcclusal: [],
        mandOcclusal: [],
        mallampati: [],
      };

      for (const key of Object.keys(ORAL_LABELS) as OralKey[]) {
        const label = ORAL_LABELS[key];

        const savedKeys = Array.isArray(savedOralImaging[label])
          ? savedOralImaging[label]
          : [];

        for (const imageKey of savedKeys) {
          if (!imageKey || typeof imageKey !== "string") continue;

          try {
            const parts = imageKey.split("/");

            if (parts.length < 3) {
              console.warn("Invalid oral imaging S3 key:", imageKey);
              continue;
            }

            // Example:
            // oral-imaging/anterior/P-2026-000020-6/file.jpg
            const module = `${parts[0]}/${parts[1]}`;
            const objectKey = parts.slice(2).join("/");

            const viewRes = await api.S3config.get("/view-url", {
              params: {
                module,
                key: objectKey,
              },
            });

            const signedUrl = viewRes.data?.url || (viewRes as any).url;

            if (!signedUrl) {
              console.warn(
                "No signed URL returned for oral imaging:",
                imageKey,
              );
              continue;
            }

            restored[key].push({
              key: imageKey,
              previewUrl: signedUrl,
              originalFilename: imageKey.split("/").pop() ?? "Uploaded image",
            });
          } catch (imageErr) {
            console.error(
              "Failed to load oral imaging image:",
              imageKey,
              imageErr,
            );
          }
        }
      }

      setUploadedUrls(restored);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        // No existing oral imaging for this patient
        return;
      }

      console.error("Failed to load existing Oral Imaging:", err);
    }
  };

  useEffect(() => {
    loadExistingOralImaging();
  }, [emrId]);

  const resetForm = () => {
    console.log("RESET FORM CALLED");

    setUploadedUrls({
      anterior: [],
      rightBuccal: [],
      leftBuccal: [],
      maxOcclusal: [],
      mandOcclusal: [],
      mallampati: [],
    });
    setResetTrigger((prev) => prev + 1); // TRIGGER CHILD RESET
  };

  // SUBMIT FORM
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setLoading(true);

    try {
      const payload: Record<string, string[]> = {};

      // Prepare final MongoDB body
      (Object.keys(uploadedUrls) as OralKey[]).forEach((key) => {
        const imageKeys = uploadedUrls[key].map((image) => image.key);

        if (imageKeys.length > 0) {
          payload[ORAL_LABELS[key]] = imageKeys;
        }
      });

      await api.dataCollectionApi.post("/api/forms/oral-imaging", {
        emrId,
        "Oral Imaging": payload,
      });

      alert("Oral imaging submitted successfully!");
      // ✅ only after successful validation
      onComplete && onComplete();
      resetForm();
    } catch (err: any) {
      console.error(err);
      alert(
        "Failed to submit oral imaging: " +
          (err.response?.data?.message ?? err.message),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="oral-form" onSubmit={handleSubmit}>
      {/* Header */}
      <div className="vs-section-bar">Oral Imaging</div>

      {/* Grid Layout */}
      <div className="oral-pairs-grid">
        {[
          ["anterior", "rightBuccal"],
          ["leftBuccal", "maxOcclusal"],
          ["mandOcclusal", "mallampati"],
        ].map((pair, i) => (
          <div className="oral-row" key={i}>
            {pair.map((key) => {
              const typedKey = key as OralKey;

              return (
                <div className="oral-pair-card" key={typedKey}>
                  <div className="oral-card-title">{ORAL_LABELS[typedKey]}</div>

                  <div className="oral-pair-content">
                    {/* Preview shows MULTIPLE IMAGES */}
                    <div className="oral-image-preview">
                      <img
                        src={ORAL_DEFAULT_IMAGES[typedKey]}
                        alt={ORAL_LABELS[typedKey]}
                        className="oral-image"
                      />
                    </div>

                    {/* Upload */}

                    <div className="oral-upload-box">
                      <S3ImageUploadBox
                        emrId={emrId}
                        moduleName={`oral-imaging/${ORAL_S3_MODULE_MAP[typedKey]}`}
                        label={ORAL_LABELS[typedKey]}
                        resetTrigger={resetTrigger}
                        onUploaded={(images) =>
                          setUploadedUrls((prev) => ({
                            ...prev,
                            [typedKey]: [...prev[typedKey], ...images],
                          }))
                        }
                      />

                      {uploadedUrls[typedKey].length > 0 && (
                        <div className="oral-uploaded-list">
                          {uploadedUrls[typedKey].map((image, index) => (
                            <div key={index} className="oral-uploaded-item">
                              <img
                                src={image.previewUrl}
                                alt={image.originalFilename}
                                className="oral-uploaded-thumbnail"
                              />

                              <div className="oral-uploaded-details">
                                <span className="oral-uploaded-name">
                                  {image.originalFilename ??
                                    `Image ${index + 1}`}
                                </span>

                                <button
                                  type="button"
                                  className="oral-remove-btn"
                                  onClick={() =>
                                    removeUploadedImage(typedKey, index)
                                  }
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="audio-actions">
        <button type="button" className="audio-btn audio-btn-secondary">
          Close
        </button>
        <button
          type="submit"
          className="audio-btn audio-btn-primary"
          disabled={loading}
        >
          {loading ? "Submitting..." : "Submit Assessment"}
        </button>
      </div>
    </form>
  );
};

export default OralImagingForm;
